import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Container,
    Alert,
    Autocomplete,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import { red } from '@mui/material/colors';

import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import axios from 'axios';
import { internshipDomain, phonepeDomain } from '../util/domain';
import { debounce, min } from 'lodash';

const RegistrationForm = () => {
    const { eventHashId } = useParams();
    const navigate = useNavigate();
    // UTM fields
    const [utmParams, setUtmParams] = useState({
        utm_source: null,
        utm_medium: null,
        utm_campaign: null
    });
    const [event, setEvent] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        gender: '',
        otherGender: '',
        degree: '',
        otherDegree: '',
        yop: '',
        otherYear: '',
        collegeName: '',
        college_code: '',
        branch: '',
        otherBranch: '',
        state: '',
        otherState: '',
        city: '',
        preferredLocation: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [existingStudentId, setExistingStudentId] = useState(null);
    const [states, setStates] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Refs for "Other" input fields
    const otherYearRef = React.useRef(null);
    const otherStateRef = React.useRef(null);
    const otherDegreeRef = React.useRef(null);
    const otherGenderRef = React.useRef(null);
    const otherBranchRef = React.useRef(null);

    // Add 'Other' option to colleges list for Autocomplete
    const [isOtherCollegeSelected, setIsOtherCollegeSelected] = useState(false);
    const collegesWithOther = formData.state && formData.state !== 'Other'
        ? [...colleges, { college_name: 'Other', college_code: '' }]
        : colleges;

    // Generate year options dynamically
    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        // Add 3 previous years
        for (let i = 3; i >= 1; i--) {
            years.push((currentYear - i).toString());
        }
        // Add current year
        years.push(currentYear.toString());
        // Add 2 future years
        for (let i = 1; i <= 2; i++) {
            years.push((currentYear + i).toString());
        }
        // Add "Other" option
        years.push("Other");
        return years;
    };

    // Capture UTM params on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setUtmParams({
            utm_source: params.get('utm_source') || null,
            utm_medium: params.get('utm_medium') || null,
            utm_campaign: params.get('utm_campaign') || null
        });
    }, []);

    useEffect(() => {
        fetchEventDetails();
        fetchStates();
    }, [eventHashId]);
    const fetchEventDetails = async () => {
        try {
            const response = await axios.get(`${internshipDomain}/api/events/${eventHashId}`);
            if (response.data.success) {
                const eventData = response.data.event;
                // Check if registration is closed
                if (eventData.registration_status === 'CLOSED') {
                    navigate('/');
                    return;
                }
                setEvent(eventData);
                console.log("event:", eventData);
            } else {
                setError('Event not found');
                navigate('/');
            }
        } catch (error) {
            setError('Failed to fetch event details');
            navigate('/');
        }
    };

    const fetchStates = async () => {
        try {
            const response = await axios.get(`${internshipDomain}/api/college/states`);
            if (response.data.success) {
                setStates(response.data.states);
            }
        } catch (error) {
            console.error('Failed to fetch states:', error);
            setError('Failed to fetch states');
        }
    };

    // Debounced search function
    const debouncedFetchColleges = useCallback(
        debounce(async (stateId, searchQuery, pageNum) => {
            try {
                const response = await axios.get(
                    `${internshipDomain}/api/college/colleges/${stateId}`,
                    {
                        params: {
                            page: pageNum,
                            limit: 20,
                            search: searchQuery
                        }
                    }
                );
                if (response.data.success) {
                    if (pageNum === 1) {
                        setColleges(response.data.colleges);
                    } else {
                        setColleges(prev => [...prev, ...response.data.colleges]);
                    }
                    setHasMore(pageNum < response.data.pagination.totalPages);
                }
            } catch (error) {
                console.error('Failed to fetch colleges:', error);
                setError('Failed to fetch colleges');
            }
        }, 300),
        []
    );

    const fetchColleges = async (stateId, searchQuery = '', pageNum = 1) => {
        setLoading(true);
        try {
            await debouncedFetchColleges(stateId, searchQuery, pageNum);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        if (e.target.name === 'mobile') {
            // Remove any non-digit characters and get last 10 digits
            const cleanedNumber = e.target.value.replace(/\D/g, '');
            const last10Digits = cleanedNumber.slice(-10);

            // Update form data
            setFormData({
                ...formData,
                [e.target.name]: last10Digits
            });

            // Real-time validation for mobile number
            if (last10Digits.length > 0 && last10Digits.length !== 10) {
                setErrors(prev => ({
                    ...prev,
                    mobile: 'Please enter a valid 10-digit mobile number'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    mobile: ''
                }));
            }
        } else if (e.target.name === 'otherYear') {
            const yearValue = e.target.value;
            // Only allow digits
            const cleanedYear = yearValue.replace(/\D/g, '');

            // Update form data
            setFormData({
                ...formData,
                [e.target.name]: cleanedYear
            });

            // Real-time validation for year
            if (cleanedYear.length > 0) {
                if (cleanedYear.length !== 4) {
                    setErrors(prev => ({
                        ...prev,
                        otherYear: 'Year must be exactly 4 digits'
                    }));
                } else {
                    const minYear = new Date().getFullYear() - 10;
                    const maxYear = new Date().getFullYear() + 5;
                    const yearNum = parseInt(cleanedYear, 10);
                    if (yearNum < minYear || yearNum > maxYear) {
                        setErrors(prev => ({
                            ...prev,
                            otherYear: `Please enter a valid year between ${minYear} and ${maxYear}`
                        }));
                    } else {
                        setErrors(prev => ({
                            ...prev,
                            otherYear: ''
                        }));
                    }
                }
            } else {
                setErrors(prev => ({
                    ...prev,
                    otherYear: ''
                }));
            }
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value,
            });
        }
    };


    const validateForm = () => {
        const newErrors = {};

        // Check required fields
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else {
            // Clean the mobile number and validate
            const cleanedMobile = formData.mobile.replace(/\D/g, '').slice(-10);
            if (cleanedMobile.length !== 10) {
                newErrors.mobile = 'Please enter a valid 10-digit mobile number';
            }
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }
        if (formData.gender === 'other' && !formData.otherGender.trim()) {
            newErrors.otherGender = 'Please specify gender';
        }

        if (!formData.degree) {
            newErrors.degree = 'Degree is required';
        }
        if (formData.degree === 'Other' && !formData.otherDegree.trim()) {
            newErrors.otherDegree = 'Please specify degree';
        }

        if (!formData.yop) {
            newErrors.yop = 'Year of Passing is required';
        }
        if (formData.yop === 'Other' && !formData.otherYear.trim()) {
            newErrors.otherYear = 'Please specify year';
        } else if (formData.yop === 'Other') {
            // Check if otherYear is exactly 4 digits
            if (!/^\d{4}$/.test(formData.otherYear)) {
                newErrors.otherYear = 'Year must be exactly 4 digits';
            }
            // Check if it's a reasonable year (between 1900 and 2100)
            else {
                const minYear = new Date().getFullYear() - 10;
                const maxYear = new Date().getFullYear() + 5;
                const yearValue = parseInt(formData.otherYear, 10);
                if (yearValue < minYear || yearValue > maxYear) {
                    newErrors.otherYear = `Please enter a valid year between ${minYear} and ${maxYear}`;
                }
            }
        }

        if (!formData.collegeName.trim()) {
            newErrors.collegeName = 'College name is required';
        }

        if (!formData.branch) {
            newErrors.branch = 'Branch is required';
        }
        if (formData.branch === 'Other' && !formData.otherBranch.trim()) {
            newErrors.otherBranch = 'Please specify branch';
        }

        if (!formData.state) {
            newErrors.state = 'State is required';
        }
        if (formData.state === 'Other' && !formData.otherState.trim()) {
            newErrors.otherState = 'Please specify state';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (event && event.event_type === 'INTERNSHIP' && !formData.preferredLocation) {
            newErrors.preferredLocation = 'Preferred internship location is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Returns true if no errors
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        setError('');

        try {
            // Prepare the final form data
            let finalCollegeName = formData.collegeName;
            let finalCollegeCode = formData.college_code;
            if (
                formData.state !== 'Other' &&
                collegesWithOther.find(c => c.college_name === 'Other') &&
                (collegesWithOther.find(c => c.college_name === formData.collegeName) === undefined || formData.collegeName === '')
            ) {
                // User entered their own college name in the text field
                finalCollegeCode = null;
            }
            const finalFormData = {
                ...formData,
                studentHashId: existingStudentId, // Include existing student ID if available
                // Use otherState value if state is "Other"
                state: formData.state === 'Other' ? formData.otherState : formData.state,
                // Use otherYear value if yop is "Other"
                yop: formData.yop === 'Other' ? formData.otherYear : formData.yop,
                // College fields
                collegeName: finalCollegeName,
                college_code: finalCollegeCode,
                // UTM params if present
                utm_source: utmParams.utm_source || null,
                utm_medium: utmParams.utm_medium || null,
                utm_campaign: utmParams.utm_campaign || null
            };

            const response = await axios.post(`${internshipDomain}/api/registration/${eventHashId}`, finalFormData);

            if (response.data.success) {
                if ((response.data.mobile_verified === true || response.data.mobile_verified === 1) && (event.registration_fee === 0)) {
                    navigate(`/thank-you/${response.data.registrationHashId}`);
                } else if ((response.data.mobile_verified === true || response.data.mobile_verified === 1) && (event.registration_fee > 0)) {
                    window.location.href = `${phonepeDomain}/payment/${response.data.registrationHashId}`;
                } else {
                    navigate(`/verify-otp/${response.data.registrationHashId}`);
                }
            } else if (response.status === 200) {
                // Fallback case - check if mobile is not verified (could be false or 0)
                if (response.data.mobile_verified === false || response.data.mobile_verified === 0) {

                } else {
                    window.location.href = `${phonepeDomain}/payment/${response.data.registrationHashId}`;
                }
            } else if (response.status === 400) {
                //Registration closed
                setError(response.data.message);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    if (!event) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Typography>Loading event details...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: 1 }}>
                <img
                    src={`${internshipDomain}/workshop/${eventHashId}.jpeg`}
                    alt="Vector India"
                    style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 8
                    }}
                />
            </Box>
            <Paper sx={{ p: 1 }}>
                {event.html_template && (
                    < Box
                        sx={{
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                color: '#ff8800',
                                textAlign: 'center'
                            },
                            '& p': {
                                textAlign: 'center'
                            },
                            '& ul': {
                                backgroundColor: '#5f5f5f48',
                                borderRadius: '0 5px 5px 0',
                                minHeight: '250px',
                                alignContent: 'center',
                                lineHeight: '22px',
                                borderLeft: '4px solid #f47c1c'
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: event.html_template }}
                    />
                )}
                <br />
                <Typography variant="h4" component="h4" gutterBottom align="center" sx={{ color: '#ff8800' }}>
                    {event.event_type === 'INTERNSHIP' ? 'Internship' : event.event_type === 'WORKSHOP' ? 'Workshop' : 'Webinar'} Registration Form
                </Typography>

                {event.event_type !== 'INTERNSHIP' && (
                    <>
                        <Typography variant="h6" gutterBottom align="center" color="primary">
                            {event.title}
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                            {event.registration_fee > 0 && (
                                <Typography variant="body2" gutterBottom>
                                    <strong>Registration Fee:</strong> ₹{event.registration_fee}
                                </Typography>
                            )}
                            <Typography variant="h6" gutterBottom>
                                <strong>{event.event_type === 'WORKSHOP' ? 'Workshop' : 'Webinar'} Exam Dates:</strong> {new Date(event.event_start_date).toLocaleDateString()} {new Date(event.event_start_date).toLocaleTimeString()} - {new Date(event.event_end_date).toLocaleDateString()} {new Date(event.event_end_date).toLocaleTimeString()}
                            </Typography>
                        </Box>
                    </>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Please enter your full name."
                        name="name"
                        autoComplete="name"
                        color="warning"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="mobile"
                        label="Mobile Number"
                        name="mobile"
                        color="warning"
                        autoComplete="tel"
                        value={formData.mobile}
                        onChange={handleChange}
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        inputProps={{
                            maxLength: 10,
                            pattern: "[0-9]{10}",
                            inputMode: "numeric"
                        }}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        color="warning"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email}
                    />

                    <Autocomplete
                        id="gender"
                        options={["Male", "Female", "Other"]}
                        value={formData.gender || null}
                        onChange={(event, newValue) => {
                            setFormData({
                                ...formData,
                                gender: newValue || ''
                            });

                            if (newValue === 'Other') {
                                // Focus on the otherGender field
                                setTimeout(() => {
                                    otherGenderRef.current?.focus();
                                }, 0);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="normal"
                                color="warning"
                                required
                                fullWidth
                                label="Gender"
                                name="gender"
                                error={!!errors.gender}
                                helperText={errors.gender}
                            />
                        )}
                    />

                    {formData.gender === 'Other' && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otherGender"
                            label="Please specify gender"
                            name="otherGender"
                            value={formData.otherGender}
                            color="warning"
                            onChange={handleChange}
                            error={!!errors.otherGender}
                            helperText={errors.otherGender}
                            inputRef={otherGenderRef}
                        />
                    )}

                    <Autocomplete
                        id="degree"
                        options={[
                            "B.E",
                            "B.Tech",
                            "M.E",
                            "M.Tech",
                            "BCA",
                            "MCA",
                            "B.Sc",
                            "M.Sc",
                            "Other"
                        ]}
                        value={formData.degree || null}
                        onChange={(event, newValue) => {
                            setFormData({
                                ...formData,
                                degree: newValue || ''
                            });

                            if (newValue === 'Other') {
                                // Focus on the otherDegree field
                                setTimeout(() => {
                                    otherDegreeRef.current?.focus();
                                }, 0);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="normal"
                                color="warning"
                                required
                                fullWidth
                                label="Degree"
                                name="degree"
                                error={!!errors.degree}
                                helperText={errors.degree}
                            />
                        )}
                    />

                    {formData.degree === 'Other' && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otherDegree"
                            label="Please specify degree"
                            name="otherDegree"
                            value={formData.otherDegree}
                            color="warning"
                            onChange={handleChange}
                            error={!!errors.otherDegree}
                            helperText={errors.otherDegree}
                            inputRef={otherDegreeRef}
                        />
                    )}

                    <FormControl fullWidth margin="normal" required error={!!errors.yop}>
                        <InputLabel id="yop-label" color="warning">Year of Passing</InputLabel>
                        <Select
                            labelId="yop-label"
                            id="yop"
                            name="yop"
                            value={formData.yop || ''}
                            label="Year of Passing"
                            color="warning"
                            onChange={(e) => {
                                handleChange(e);
                                if (e.target.value === 'Other') {
                                    // Use setTimeout to ensure the otherYear field is rendered before focusing
                                    setTimeout(() => {
                                        otherYearRef.current?.focus();
                                    }, 0);
                                }
                            }}
                        >
                            {generateYearOptions().map((year) => (
                                <MenuItem key={year} value={year}>
                                    {year}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.yop && <FormHelperText>{errors.yop}</FormHelperText>}
                    </FormControl>

                    {formData.yop === 'Other' && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otherYear"
                            label="Please specify year (4 digits)"
                            name="otherYear"
                            value={formData.otherYear}
                            color="warning"
                            onChange={handleChange}
                            inputProps={{
                                maxLength: 4,
                                pattern: "[0-9]{4}",
                                inputMode: "numeric"
                            }}
                            error={!!errors.otherYear}
                            helperText={errors.otherYear}
                            inputRef={otherYearRef}
                        />
                    )}

                    <FormControl
                        fullWidth
                        margin="normal"
                        error={!!errors.state}
                    >
                        <InputLabel id="state-select-label">State</InputLabel>
                        <Select
                            labelId="state-select-label"
                            id="state-select"
                            color="warning"
                            value={formData.state}
                            label="State"
                            onChange={(e) => {
                                const selectedValue = e.target.value;
                                setFormData({
                                    ...formData,
                                    state: selectedValue,
                                    collegeName: ''
                                });

                                if (selectedValue !== 'Other') {
                                    const selectedState = states.find(state => state.name === selectedValue);
                                    if (selectedState) {
                                        fetchColleges(selectedState.id);
                                    }
                                } else {
                                    // Clear colleges when "Other" is selected
                                    setColleges([]);

                                    // Focus on the otherState field
                                    setTimeout(() => {
                                        otherStateRef.current?.focus();
                                    }, 0);
                                }
                            }}
                            IconComponent={KeyboardArrowDown}
                        >
                            {states.map((state) => (
                                <MenuItem
                                    key={state.id}
                                    value={state.name}
                                >
                                    {state.name}
                                </MenuItem>
                            ))}
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {errors.state && (
                            <FormHelperText>{errors.state}</FormHelperText>
                        )}
                    </FormControl>

                    {formData.state === 'Other' && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otherState"
                            label="Please specify state"
                            name="otherState"
                            value={formData.otherState}
                            color="warning"
                            onChange={handleChange}
                            error={!!errors.otherState}
                            helperText={errors.otherState}
                            inputRef={otherStateRef}
                        />
                    )}

                    <Autocomplete
                        id="collegeName"
                        options={collegesWithOther}
                        getOptionLabel={(option) => option.college_name || ''}
                        loading={loading}
                        onInputChange={(event, newInputValue, reason) => {
                            // Only search if not in 'Other' mode and not typing in the 'Other' input
                            if (!isOtherCollegeSelected && formData.state && formData.state !== 'Other') {
                                setSearchText(newInputValue);
                                const selectedState = states.find(state => state.name === formData.state);
                                if (selectedState) {
                                    setPage(1);
                                    fetchColleges(selectedState.id, newInputValue, 1);
                                }
                            }
                        }}
                        filterOptions={(x) => x}
                        ListboxProps={{
                            onScroll: (event) => {
                                const listboxNode = event.currentTarget;
                                if (
                                    listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 &&
                                    hasMore &&
                                    !loading
                                ) {
                                    const selectedState = states.find(state => state.name === formData.state);
                                    if (selectedState) {
                                        const nextPage = page + 1;
                                        setPage(nextPage);
                                        fetchColleges(selectedState.id, searchText, nextPage);
                                    }
                                }
                            },
                        }}
                        value={
                            formData.state === 'Other'
                                ? null
                                : isOtherCollegeSelected
                                    ? { college_name: 'Other', college_code: '' }
                                    : (formData.collegeName && collegesWithOther.length > 0
                                        ? collegesWithOther.find(college => college.college_name === formData.collegeName) || null
                                        : null)
                        }
                        onChange={(event, newValue) => {
                            if (newValue && newValue.college_name === 'Other') {
                                setIsOtherCollegeSelected(true);
                                setFormData({
                                    ...formData,
                                    collegeName: '',
                                    college_code: ''
                                });
                            } else {
                                setIsOtherCollegeSelected(false);
                                setFormData({
                                    ...formData,
                                    collegeName: newValue ? newValue.college_name : '',
                                    college_code: newValue ? newValue.college_code : ''
                                });
                            }
                        }}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#FF8800',
                                            fontSize: '1rem',
                                            mb: 1,
                                            textAlign: 'left'
                                        }}
                                        noWrap
                                    >
                                        {option.college_name}
                                    </Typography>
                                    {option.univerisity_name && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <SchoolIcon sx={{ fontSize: '1rem', color: '#000000', mr: 1 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#555555',
                                                    fontSize: '0.875rem',
                                                    flex: 1
                                                }}
                                                noWrap
                                            >
                                                {option.univerisity_name}
                                            </Typography>
                                        </Box>
                                    )}
                                    {option.college_address && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <LocationOnIcon sx={{ fontSize: '1rem', color: '#f44336', mr: 1 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#555555',
                                                    fontSize: '0.875rem',
                                                    flex: 1
                                                }}
                                                noWrap
                                            >
                                                {option.college_address}
                                            </Typography>
                                        </Box>
                                    )}
                                    {option.college_website && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <LanguageIcon sx={{ fontSize: '1rem', color: '#03a9f4', mr: 1 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#555555',
                                                    fontSize: '0.875rem',
                                                    flex: 1
                                                }}
                                                noWrap
                                            >
                                                {option.college_website}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                        hidden={!formData.state || formData.state === 'Other'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="normal"
                                required
                                color="warning"
                                fullWidth
                                label="College Name"
                                name="collegeName"
                                error={!!errors.collegeName}
                                helperText={errors.collegeName || (!formData.state && 'Please select a state first')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading && <CircularProgress color="inherit" size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Show input if user selects 'Other' in college autocomplete */}
                    {formData.state !== 'Other' && isOtherCollegeSelected && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="collegeNameOther"
                            label="Please specify your college name"
                            name="collegeNameOther"
                            value={formData.collegeName}
                            color="warning"
                            onChange={e => setFormData({ ...formData, collegeName: e.target.value })}
                            error={!!errors.collegeName}
                            helperText={errors.collegeName}
                        />
                    )}

                    <Autocomplete
                        id="branch"
                        options={[
                            "ECE",
                            "ETC",
                            "EE",
                            "EEE",
                            "EIE",
                            "CSE",
                            "AIML",
                            "AIDS",
                            "CYBER SECURITY",
                            "IT",
                            "Civil",
                            "Mech",
                            "Other"
                        ]}
                        value={formData.branch || null}
                        onChange={(event, newValue) => {
                            setFormData({
                                ...formData,
                                branch: newValue || ''
                            });
                            if (newValue === 'Other') {
                                // Focus on the otherDegree field
                                setTimeout(() => {
                                    otherBranchRef.current?.focus();
                                }, 0);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="normal"
                                required
                                fullWidth
                                label="Branch"
                                color="warning"
                                name="branch"
                                error={!!errors.branch}
                                helperText={errors.branch}
                            />
                        )}
                    />

                    {formData.branch === 'Other' && (
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="otherBranch"
                            label="Please specify branch"
                            color="warning"
                            name="otherBranch"
                            value={formData.otherBranch}
                            onChange={handleChange}
                            inputRef={otherBranchRef}
                            error={!!errors.otherBranch}
                            helperText={errors.otherBranch}
                        />
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="city"
                        label="City/Town"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        color="warning"
                        error={!!errors.city}
                        helperText={errors.city}
                    />

                    {event && event.event_type === 'INTERNSHIP' && (
                        <Autocomplete
                            id="preferredLocation"
                            options={["Hyderabad", "Bangalore", "Chennai"]}
                            value={formData.preferredLocation || null}
                            onChange={(e, newValue) => {
                                setFormData({
                                    ...formData,
                                    preferredLocation: newValue || ''
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="normal"
                                    required
                                    fullWidth
                                    color="warning"
                                    label="Preferred Internship Location"
                                    name="preferredLocation"
                                    error={!!errors.preferredLocation}
                                    helperText={errors.preferredLocation}
                                />
                            )}
                        />
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                minWidth: 200,
                                backgroundColor: '#ff6b00',
                                '&:hover': {
                                    backgroundColor: '#ff8533'
                                }
                            }}
                        >
                            {loading ? 'Submitting...' : 'Register Now'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container >
    );
};

export default RegistrationForm; 